import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Play, ScrollText } from "lucide-react";
import { Toaster } from "sonner";
import { useTheme } from "@client/hooks/use-theme";
import { Button } from "@tubo/ui/components/ui/button";
import { CleanProfileEditor } from "@client/components/data-center/cleaning/clean-profile-editor";
import { StartRunDialog } from "@client/components/data-center/cleaning/start-run-dialog";

/**
 * 清洗方案编辑页（全屏独立工作台）：
 * 根层级路由（不经过 _app shell），无侧边栏与数据中心模块头——左侧步骤链与右侧
 * 实时预览需要尽量宽的横向空间（先例：数据抽取审核工作台 /data-center/review/$taskId）。
 * 自带 Toaster（全局 Toaster 挂在 _app 布局层，根级路由取不到）。
 */
export const Route = createFileRoute("/data-center/cleaning/$id")({
  component: CleanProfileDetailPage,
});

function CleanProfileDetailPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const { resolvedTheme } = useTheme();
  const isNew = id === "new";
  const [startOpen, setStartOpen] = useState(false);

  return (
    <div className="flex h-screen flex-col overflow-hidden">
      <Toaster position="top-center" theme={resolvedTheme} closeButton toastOptions={{ duration: 5000 }} />
      <div className="flex items-center gap-2 border-b px-4 py-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate({ to: "/data-center/cleaning", search: { tab: "profiles", profileId: undefined } })}
        >
          <ArrowLeft className="size-4" />
        </Button>
        <h1 className="text-lg font-semibold">{isNew ? "新建清洗方案" : "清洗方案"}</h1>
        <span className="flex-1" />
        {!isNew ? (
          <>
            <Button
              variant="outline"
              onClick={() =>
                void navigate({
                  to: "/data-center/cleaning",
                  search: { tab: "runs", profileId: id },
                })
              }
            >
              <ScrollText className="size-4" /> 该方案的运行记录
            </Button>
            <Button onClick={() => setStartOpen(true)}>
              <Play className="size-4" /> 发起运行
            </Button>
          </>
        ) : null}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        <CleanProfileEditor
          id={isNew ? undefined : id}
          onSaved={(savedId) => navigate({ to: "/data-center/cleaning/$id", params: { id: savedId } })}
        />
      </div>
      {!isNew ? (
        <StartRunDialog
          open={startOpen}
          onOpenChange={setStartOpen}
          defaultProfileId={id}
          onStarted={() =>
            void navigate({ to: "/data-center/cleaning", search: { tab: "runs", profileId: id } })
          }
        />
      ) : null}
    </div>
  );
}
